// services
import { askChatGPT } from '@/services/workflow.service';
// helpers
import { extractCodeBlock, showToaster } from '@/utils/helpers';

export const getFormElements = (task, isVueBasedFormBuilder = false) => {
  return isVueBasedFormBuilder
    ? task.props.elements
    : task.props.formTrigger.elements;
};

export const normalizePreviousTaskId = (value) => {
  if (
    value === null ||
    value === undefined ||
    ['', 'none', 'null'].includes(String(value).trim().toLowerCase())
  ) {
    return null;
  }

  const parsedValue = Number.parseInt(value, 10);
  return Number.isNaN(parsedValue) ? null : parsedValue;
};

export const getSortedFlowTasks = (tasks = []) => {
  if (!Array.isArray(tasks)) return [];
  return [...tasks].sort((firstTask, secondTask) => {
    const firstPreviousTaskId = normalizePreviousTaskId(
      firstTask?.previousTaskId
    );
    const secondPreviousTaskId = normalizePreviousTaskId(
      secondTask?.previousTaskId
    );

    const firstHasPrevious = firstPreviousTaskId !== null;
    const secondHasPrevious = secondPreviousTaskId !== null;

    if (firstHasPrevious !== secondHasPrevious) {
      return Number(firstHasPrevious) - Number(secondHasPrevious);
    }

    return (firstPreviousTaskId || 0) - (secondPreviousTaskId || 0);
  });
};

export const getFormStructure = (task, isVueBasedFormBuilder = false) => {
  return isVueBasedFormBuilder
    ? task.props.structure
    : task.props.formTrigger.structure;
};

export const executeAIGeneratedStyle = (styles = '') => {
  const styleId = 'ai-generated-form-styles';
  const scopedStyles = getScopedAiStyles(styles);
  let styleTag = document.getElementById(styleId);
  if (!scopedStyles.trim()) {
    if (styleTag) styleTag.remove();
    return;
  }

  if (!styleTag) {
    styleTag = document.createElement('style');
    styleTag.id = styleId;
    document.head.appendChild(styleTag);
  }

  styleTag.textContent = scopedStyles;
};

export const resolveResponseType = (responseType, workflowResponse) => {
  if (responseType) return responseType;
  if (workflowResponse === null || workflowResponse === undefined) return null;
  return typeof workflowResponse === 'string' ? 'html' : 'json';
};

export const isHtmlResponseType = (responseType) => responseType === 'html';
export const isJsonResponseType = (responseType) => responseType === 'json';
export const isFormResponseType = (responseType) => responseType === 'form';

export const removeAIGeneratedStyle = (styles = null) => {
  const styleId = 'ai-generated-form-styles';
  let styleTag = document.getElementById(styleId);
  if (!styleTag) return;

  if (typeof styles === 'string') {
    const expectedStyles = getScopedAiStyles(styles).trim();
    const currentStyles = (styleTag.textContent || '').trim();
    if (currentStyles !== expectedStyles) {
      return;
    }
  }

  styleTag.remove();
};

const sanitizeAiStyles = (styles = '') => {
  if (!styles) return '';
  let safeStyles = String(styles);
  const blockedPatterns = [
    /@import/gi,
    /expression\s*\(/gi,
    /javascript:/gi,
    /behavior\s*:/gi,
    /<\/?script/gi,
  ];
  blockedPatterns.forEach((pattern) => {
    safeStyles = safeStyles.replace(pattern, '');
  });
  return safeStyles;
};

const scopeAiStyles = (styles = '', rootSelector = '#codelosForm') => {
  if (!styles?.trim()) return '';
  return styles.replace(
    /(^|})\s*([^@{}][^{}]*)\{/g,
    (_match, prefix, selector) => {
      const scopedSelector = selector
        .split(',')
        .map((singleSelector) => {
          const trimmed = singleSelector.trim();
          if (!trimmed) return trimmed;
          if (
            trimmed.startsWith(rootSelector) ||
            trimmed.startsWith('html') ||
            trimmed.startsWith('body') ||
            trimmed.startsWith(':root')
          ) {
            return trimmed;
          }
          return `${rootSelector} ${trimmed}`;
        })
        .join(', ');
      return `${prefix} ${scopedSelector}{`;
    }
  );
};

const getScopedAiStyles = (styles = '') =>
  scopeAiStyles(sanitizeAiStyles(styles));

export const createAIGeneratedEntry = (
  parsedResponse,
  prompt = '',
  targetElementId = null
) => {
  return {
    id: `ai-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    createdAt: new Date().toISOString(),
    targetElementId,
    prompt,
    ...parsedResponse,
  };
};

export const resolveAIEntryIndex = (aiList = [], entryIdOrIndex = null) => {
  if (!Array.isArray(aiList) || aiList.length === 0) return -1;
  if (typeof entryIdOrIndex === 'number') return entryIdOrIndex;
  if (!entryIdOrIndex) return -1;
  return aiList.findIndex((item) => item?.id === entryIdOrIndex);
};

export const handleAskAI = async (
  task,
  prompt,
  flowRunnerEndpoint,
  selectedElement = null,
  editTaskIndex = null
) => {
  try {
    const previousCode = task?.props?.settings?.aiGenerated?.[editTaskIndex];
    const formData = new FormData();
    formData.append(
      'props',
      JSON.stringify({
        prompt: prompt,
        metaInfo: {
          elements: task.props?.elements,
          previousCode: previousCode || '',
          selectedElement,
        },
        model: 'gpt-5.4-mini',
      })
    );
    formData.append('usecase', 'ailogic_form');

    const response = await askChatGPT(flowRunnerEndpoint, formData);

    return response.data.message;
  } catch (error) {
    throw error;
  }
};

export const getFormPromptSuggestions = async (task, flowRunnerEndpoint) => {
  try {
    const formData = new FormData();
    formData.append(
      'props',
      JSON.stringify({
        metaInfo: { elements: task.props?.elements ?? [] },
        model: 'gpt-3.5-turbo',
      })
    );
    formData.append('usecase', 'form_prompt_suggetions');

    const response = await askChatGPT(flowRunnerEndpoint, formData);
    return response.data.message;
  } catch (error) {
    throw error;
  }
};

export const getParsedAIResponse = (aiResponse, task) => {
  // Prefer fenced code content; fall back to raw response when no code block exists
  const contentToParse = extractCodeBlock(aiResponse, 'json') || aiResponse;
  const gptResponse = JSON.parse(contentToParse);
  const newLogic = gptResponse.code?.trim() || '';
  const newLogicExplanation = gptResponse.explanation?.trim() || '';

  const newStyle = gptResponse.styles?.trim() || '';
  const newStyleExplanation = gptResponse.styleExplanation?.trim() || '';

  const oldLogicCode = task.props?.settings?.aiGenerated?.code || '';
  const oldLogicExplanation =
    task.props?.settings?.aiGenerated?.explanation || '';

  const oldStyleCode = task.props?.settings?.aiGenerated?.styles || '';
  const oldStyleExplanation =
    task.props?.settings?.aiGenerated?.styleExplanation || '';

  const aiGeneratedValues = {
    code:
      gptResponse.isReplace || 1 === 1
        ? newLogic
        : oldLogicCode
        ? `${oldLogicCode}\n\n${newLogic}`
        : newLogic,
    explanation:
      gptResponse.isReplace || 1 === 1
        ? newLogicExplanation
        : oldLogicExplanation
        ? `${oldLogicExplanation}\n\n${newLogicExplanation}`
        : newLogicExplanation,
    styles:
      gptResponse.isStyleReplace || 1 === 1
        ? newStyle
        : oldStyleCode
        ? `${oldStyleCode}\n\n${newStyle}`
        : newStyle,
    styleExplanation:
      gptResponse.isStyleReplace || 1 === 1
        ? newStyleExplanation
        : oldStyleExplanation
        ? `${oldStyleExplanation}\n\n${newStyleExplanation}`
        : newStyleExplanation,
  };

  return aiGeneratedValues;
};

export const checkboxElementValidation = (
  vueInstance,
  element,
  isToast = false,
  isPreview = false
) => {
  const totalChecked = element.options.filter(
    (option) => option.checked
  ).length;
  if (
    (element.settings.min !== '' &&
      (element.settings.min < 0 ||
        element.settings.min > element.options.length ||
        !/^[0-9]+$/.test(element.settings.min))) ||
    (element.settings.max !== '' &&
      (element.settings.max < 0 ||
        element.settings.max > element.options.length ||
        !/^[0-9]+$/.test(element.settings.max))) ||
    (element.settings.min !== '' &&
      element.settings.max !== '' &&
      element.settings.min > element.settings.max)
  ) {
    return false;
  }
  if (element.settings.min !== '' && totalChecked < element.settings.min) {
    if (isToast) {
      const optionCountText = `${element.settings.min} ${
        element.settings.min === 1 ? 'Option' : 'Optionen'
      }`;

      showToaster(
        vueInstance,
        isPreview
          ? `Bitte wählen Sie mindestens ${optionCountText} in '${element.label}' aus.`
          : `Es dürfen mindestens ${element.settings.min}“ Auswahlen ausgewählt werden.`,
        'Fehlerhaft !',
        'danger'
      );
    }
    return false;
  } else if (
    element.settings.max !== '' &&
    totalChecked > element.settings.max
  ) {
    if (isToast) {
      showToaster(
        vueInstance,
        isPreview
          ? `Es dürfen höchstens ${element.settings.max} Auswahlen in '${element.label}' getroffen werden.`
          : `Es dürfen maximal ${element.settings.max} Auswahlen ausgewählt werden.`,
        'Fehlerhaft !',
        'danger'
      );
    }
    return false;
  } else {
    return true;
  }
};
export const hasUniquePropertyValues = (array, property) => {
  const values = array
    .map((item) => item[property])
    .filter((value) => value !== null && value !== undefined && value !== '');

  return new Set(values).size === values.length;
};

export const isValidOptionElement = (
  vueInstance,
  element,
  isShowToast = false
) => {
  if (
    element.options &&
    (!hasUniquePropertyValues(element.options, 'label') ||
      !hasUniquePropertyValues(element.options, 'value') ||
      element.options.some((op) => !op?.value?.trim()?.length))
  ) {
    return false;
  } else if (
    element.type === 'checkbox' &&
    element.settings.required &&
    !element.settings.disabled
  ) {
    const isValidate = checkboxElementValidation(
      vueInstance,
      element,
      isShowToast
    );

    if (!isValidate) return isValidate;
  } else if (
    element.type === 'radio' &&
    element.settings.required &&
    !element.value
  ) {
    if (isShowToast)
      showToaster(
        vueInstance,
        `Bitte wählen Sie eine Option aus.`,
        'Fehlerhaft !',
        'danger'
      );
    return false;
  }
  return true;
};

export const findById = (elements, id) => {
  return elements.find((e) => e.id === id);
};
export const isValidEmail = (email) => {
  const emailPattern = /^([a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/;
  return emailPattern.test(email);
};
export const getCurrentDateTimeValueByType = (type) => {
  const now = new Date();

  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');

  if (type === 'date') {
    // YYYY-MM-DD (local)
    return `${year}-${month}-${day}`;
  }

  if (type === 'datetime-local') {
    // YYYY-MM-DDTHH:MM (local)
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  if (type === 'time') {
    // HH:MM (local)
    return `${hours}:${minutes}`;
  }

  return '';
};

export const createUnique = (existingNames, baseName, num = 1) => {
  const stripped = baseName.replace(/[0-9]*$/, '');
  let newName = stripped + num;
  while (existingNames.includes(newName)) {
    num++;
    newName = stripped + num;
  }
  return newName;
};

export const setDragItemImage = (event) => {
  try {
    const draggedElement = event.srcElement.parentElement;
    const dragItemCard = draggedElement.closest('.form-field-card');
    const draggedImage = dragItemCard.cloneNode(true);
    const toggleList = draggedImage.querySelector('[id^="toggle-list-"]');
    if (toggleList) toggleList.remove();
    draggedImage.setAttribute('id', 'dragged-element-clone');
    draggedImage.style.setProperty(
      'width',
      dragItemCard.offsetWidth + 'px',
      'important'
    );

    document.body.appendChild(draggedImage);
    event.dataTransfer.setDragImage(draggedImage, event.offsetX, event.offsetY);
  } catch (error) {
    console.log('setting drag itme image :', error);
  }
};
export const removeDragItemImage = () => {
  const cloneContainer = document.getElementById('dragged-element-clone');
  if (cloneContainer) {
    cloneContainer.remove();
  }
};

export const openParentToggleListById = (elementId, toggleId) => {
  const handler = document.querySelector(
    `[aria-controls="toggle-list-${toggleId}"]`
  );
  if (!handler) return;

  const openHandler = (h) => {
    if (h.classList.contains('collapsed')) {
      h.click();
    }
  };

  let parentCollapse = handler.closest('.collapse')?.previousElementSibling;
  while (parentCollapse) {
    const collapseHandler = parentCollapse.querySelector('img');
    openHandler(collapseHandler);
    parentCollapse =
      parentCollapse.closest('.collapse')?.previousElementSibling;
  }

  openHandler(handler);

  if (!elementId) {
    setTimeout(() => {
      if (handler)
        handler?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 200);
    return;
  }
  setTimeout(() => {
    const invalidEl = document.getElementById(`form-element-${elementId}`);
    if (invalidEl)
      invalidEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, 500);
};

export const openInvalidToggleList = (formEl) => {
  if (!formEl) return false;
  let isValidField = true;
  const collapseHandles = formEl.querySelectorAll('.collapsed');
  collapseHandles.forEach((handler) => {
    const collapseId = handler.getAttribute('aria-controls');
    if (collapseId) {
      const collapseDiv = document.getElementById(collapseId);
      if (collapseDiv) {
        const invalidFields = collapseDiv.querySelectorAll(':invalid');
        if (invalidFields.length > 0 && collapseDiv.style.display === 'none') {
          isValidField = false;
          handler.click();
        }
      }
    }
  });
  return isValidField;
};
export const findElementInTab = (tab, id) => {
  if (!tab) return null;

  let result = null;

  function traverse(node) {
    if (!node || result) return;

    if (node.elementId === id) {
      result = node;
      return;
    }

    if (node.childs) {
      for (let child of node.childs) {
        traverse(child);
      }
    }
  }

  for (let row of tab.rows) {
    for (let col of row.cols) {
      traverse(col);
      if (result) break;
    }
    if (result) break;
  }

  return result;
};

export const findElementWithNoChildsFromTab = (data, ids, hiddenElIds) => {
  try {
    let result = null;

    function traverse(node, tabIndex) {
      if (!node || result) return;

      if (ids.includes(node.elementId)) {
        if (node?.childs?.length) {
          const isAllChildHiddenEl = node.childs.every((el) =>
            hiddenElIds.includes(el.elementId)
          );
          if (isAllChildHiddenEl) {
            result = { id: node.elementId, tabIndex, hasChild: true };
            return;
          }
        } else {
          result = { id: node.elementId, tabIndex, hasChild: false };
          return;
        }
      }

      if (node.childs) {
        for (const child of node.childs) {
          traverse(child, tabIndex);
        }
      }
    }
    for (let index = 0; index < data.length; index++) {
      const tab = data[index];
      for (const row of tab.rows) {
        for (const col of row.cols) {
          traverse(col, index);
          if (result) break;
        }
        if (result) break;
      }
      if (result) break;
    }

    return result;
  } catch (error) {
    return null;
  }
};

export const findElementLevelInTabStructure = (tabItem, targetId) => {
  function dfs(rows, level) {
    for (const row of rows) {
      for (const element of row.cols) {
        if (element.elementId === targetId) {
          return level;
        }
        if (element.childs && element.childs.length > 0) {
          const found = dfs([{ cols: element.childs }], level + 1);
          if (found !== null) return found;
        }
      }
    }
    return null;
  }

  return dfs(tabItem.rows, 0);
};

export const findParentElementId = (tabItem, targetId) => {
  function dfs(rows) {
    for (const row of rows) {
      for (const element of row.cols) {
        if (element.childs && element.childs.length > 0) {
          const found = element.childs.find(
            (child) => child.elementId === targetId
          );
          if (found) {
            return element.elementId;
          }
          const deeper = dfs([{ cols: element.childs }]);
          if (deeper !== null) return deeper;
        }
      }
    }
    return null;
  }
  return dfs(tabItem.rows);
};

const getDepthFromCol = (node, targetId, depth = 0) => {
  if (node.elementId === targetId) return depth;
  if (node.childs) {
    for (const child of node.childs) {
      const d = getDepthFromCol(child, targetId, depth + 1);
      if (d !== -1) return d;
    }
  }
  return -1;
};

const canMoveElementInTabTree = ({
  dragNode,
  dropNode,
  dropCol,
  maxDepth = 2,
  asChild = false,
}) => {
  if (!dragNode || !dropNode) return false;

  function isDescendant(node, targetId) {
    if (!node.childs) return false;
    for (let child of node.childs) {
      if (child.elementId === targetId || isDescendant(child, targetId)) {
        return true;
      }
    }
    return false;
  }

  if (isDescendant(dragNode, dropNode.elementId)) return false;

  if (!dragNode.hasOwnProperty('childs')) return true;
  const getMaxDepth = (node) => {
    if (!node.hasOwnProperty('childs')) return 0;
    if (node.childs.length === 0) return 1;
    return 1 + Math.max(...node.childs.map(getMaxDepth));
  };
  const dragDepth = getMaxDepth(dragNode);
  const colToDropDepth = getDepthFromCol(dropCol, dropNode.elementId);
  if (asChild) {
    return (
      colToDropDepth < maxDepth - 1 && colToDropDepth + dragDepth < maxDepth
    );
  } else {
    return colToDropDepth + dragDepth <= maxDepth;
  }
};

export const moveElementInTabStructure = ({
  data,
  dragId,
  dropId,
  rowIndex,
  colIndex,
  checkIsAllow = false,
  maxDepth = 2,
  position = null, // "top" or "bottom"
  createNewRow = false,
  dropTabIndex,
  isDragInsideGrid,
}) => {
  try {
    if (!dragId) return data;
    const tabList = JSON.parse(JSON.stringify(data));
    let dragNode = null;
    let dragParent = null;

    let dropNode = null;
    let dropCol = null;
    let dropParent = null;
    let dropRow = null;
    // Traverse to find nodes and their parents
    const traverse = (node, parent = null, row = null, col = null) => {
      if (node.elementId === dragId) {
        dragNode = node;
        dragParent = parent;
      }
      if (dropId && node.elementId === dropId) {
        dropParent = parent;
      }

      if (node.childs) {
        for (let child of node.childs) {
          traverse(child, node, row, col);
        }
      }
    };

    for (let tab of tabList) {
      for (let row of tab.rows) {
        for (let col of row.cols) {
          traverse(col, null, row, col);
        }
      }
    }
    if (!dragNode) return tabList;
    // find drop node
    const dropTab =
      tabList.find((tab, index) => index == dropTabIndex) || tabList[0];
    dropRow = dropTab.rows[rowIndex];
    if (!dropRow) return tabList;
    dropCol = dropRow.cols[colIndex];
    if (!dropCol) return tabList;

    if (dropCol.elementId) {
      if (
        !position &&
        dropId &&
        dropCol.elementId != dropId &&
        dropCol.hasOwnProperty('childs') &&
        dropCol?.childs?.length
      ) {
        dropNode = dropCol.childs.find((child) => child.elementId == dropId);
      } else {
        if (checkIsAllow && position) {
          function findChildRecursively(childs, dropId) {
            for (const child of childs) {
              if (child.elementId === dropId) {
                return child;
              }
              if (child.childs && child.childs.length) {
                const found = findChildRecursively(child.childs, dropId);
                if (found) return found;
              }
            }
            return null;
          }

          dropNode = findChildRecursively(dropCol.childs, dropId);
        } else {
          dropNode = {
            elementId: dropCol.elementId,
            childs: dropCol.childs || [],
          };
        }
      }
    } else {
      dropNode = dropCol;
    }
    const isAllowToMove = canMoveElementInTabTree({
      dragNode,
      dropNode,
      dropCol,
      maxDepth,
      asChild: position === null,
    });
    if (checkIsAllow) return isAllowToMove;

    if (!isAllowToMove) {
      console.log('Move not allowed');
      return tabList;
    }

    // // Remove dragNode from its current parent
    const removeFromParent = (parent, node) => {
      if (!parent) return;
      parent.childs = parent.childs.filter(
        (c) => c.elementId !== node.elementId
      );
    };
    if (dragParent) removeFromParent(dragParent, dragNode);
    else {
      for (let tab of tabList) {
        for (let row of tab.rows) {
          if (row.cols.length > 1) {
            row.cols = row.cols.map((col) => {
              if (col.elementId === dragId) {
                const { elementId, ...rest } = col;
                return rest;
              }
              return col;
            });
          } else {
            row.cols = row.cols.filter((c) => c.elementId !== dragId);
          }
        }
        tab.rows = tab.rows.filter((row) => row.cols.length);
      }
    }

    // // ---------- NEW ROW LOGIC ----------
    if (createNewRow && dropRow && dropCol) {
      let newRow = null;
      if (isDragInsideGrid) {
        const updatedDropRow = JSON.parse(JSON.stringify(dropRow));
        updatedDropRow.cols[colIndex].elementId = dragNode.elementId;
        if (dragNode.hasOwnProperty('childs')) {
          updatedDropRow.cols[colIndex].childs = dragNode.childs;
        }
        newRow = updatedDropRow;
      } else {
        newRow = { cols: [dragNode] };
      }
      const parentTab = tabList.find((tab) => tab.rows.includes(dropRow));
      const dropRowIndex = parentTab.rows.indexOf(dropRow);
      if (!parentTab) {
        return tabList;
      }
      if (position === 'top') {
        if (isDragInsideGrid) {
          parentTab.rows.splice(dropRowIndex, 1, newRow);
        } else {
          parentTab.rows.splice(dropRowIndex, 0, newRow);
        }
      } else {
        if (isDragInsideGrid) {
          parentTab.rows.splice(dropRowIndex, 1, newRow);
        } else {
          parentTab.rows.splice(dropRowIndex + 1, 0, newRow);
        }
      }

      return tabList; // DONE: return after creating new row
    }

    // // // ---------- SIBLING LOGIC ----------
    if (position === 'top' || position === 'bottom') {
      let siblingsArray = dropParent ? dropParent.childs : null;
      if (!siblingsArray) {
        for (let tab of tabList) {
          for (let row of tab.rows) {
            const index = row.cols.findIndex((c) => c.elementId === dropId);

            if (index !== -1) siblingsArray = row.cols;
          }
        }
      }

      if (siblingsArray) {
        const dropIndex = siblingsArray.findIndex(
          (c) => c.elementId === dropId
        );
        if (dropIndex !== -1) {
          if (position === 'top') siblingsArray.splice(dropIndex, 0, dragNode);
          else siblingsArray.splice(dropIndex + 1, 0, dragNode);
        }
      }
      return tabList;
    }
    // ---------- DEFAULT: move inside dropNode ----------
    dropNode.childs = dropNode.childs || [];
    dropNode.childs.push(dragNode);
    return tabList;
  } catch (error) {
    return data;
  }
};

export const moveElementToTab = (tabs, dragElementId, dropTabIndex) => {
  try {
    if (!dragElementId || dropTabIndex < 0 || dropTabIndex >= tabs.length)
      return tabs;
    const tabList = JSON.parse(JSON.stringify(tabs));
    let draggedElement = null;

    function removeElement(cols, id) {
      for (let i = 0; i < cols.length; i++) {
        if (cols[i].elementId === id) {
          if (cols.length <= 1) {
            draggedElement = cols.splice(i, 1)[0];
          } else {
            draggedElement = structuredClone(cols[i]);
            delete cols[i].elementId;
          }
          return true;
        }
        if (cols[i].childs) {
          if (removeElement(cols[i].childs, id)) return true;
        }
      }
      return false;
    }

    for (let tab of tabList) {
      for (let row of tab.rows) {
        if (removeElement(row.cols, dragElementId)) break;
      }
      if (draggedElement) break;
    }
    for (let tab of tabList) {
      tab.rows = tab.rows.filter((row) => row.cols && row.cols.length > 0);
    }

    if (!draggedElement) {
      return tabList;
    }

    const targetTab = tabList[dropTabIndex];
    if (!targetTab) {
      return tabList;
    }

    if (!targetTab?.rows?.length) {
      targetTab.rows = [];
    }

    targetTab.rows.push({ cols: [draggedElement] });

    return tabList;
  } catch (error) {
    console.log('error in moveElementToTab :', error);
    return tabs;
  }
};

export const insertElementInTabStructure = ({
  tabStructure,
  targetTabIndex,
  newElementId,
  position = null,
  referenceElementId = null,
  insertInSameCol = false,
  withChildContainer = false,
}) => {
  try {
    const tabs = JSON.parse(JSON.stringify(tabStructure));

    if (targetTabIndex < 0 || targetTabIndex >= tabs.length) return tabs;
    const activeTab = tabs[targetTabIndex];

    const newElement = withChildContainer
      ? { elementId: newElementId, childs: [] }
      : { elementId: newElementId };

    if (!referenceElementId) {
      activeTab.rows.push({ cols: [newElement] });
      return tabs;
    }

    function insertInChilds(childs, refId, newEl, pos) {
      for (let i = 0; i < childs.length; i++) {
        if (childs[i].elementId === refId) {
          if (pos === 'top') {
            childs.splice(i, 0, newEl);
          } else {
            childs.splice(i + 1, 0, newEl);
          }
          return true;
        }
        if (
          childs[i].childs &&
          insertInChilds(childs[i].childs, refId, newEl, pos)
        ) {
          return true;
        }
      }
      return false;
    }

    for (let rowIndex = 0; rowIndex < activeTab.rows.length; rowIndex++) {
      const row = activeTab.rows[rowIndex];

      // Reference found in cols
      const colIndex = row.cols.findIndex(
        (c) => c.elementId === referenceElementId
      );
      if (colIndex !== -1) {
        if (insertInSameCol) {
          if (position === 'top') {
            row.cols.splice(colIndex, 0, newElement);
          } else {
            row.cols.splice(colIndex + 1, 0, newElement);
          }
        } else {
          const newRow = { cols: [newElement] };
          if (position === 'top') {
            activeTab.rows.splice(rowIndex, 0, newRow);
          } else {
            activeTab.rows.splice(rowIndex + 1, 0, newRow);
          }
        }
        return tabs;
      }

      for (let col of row.cols) {
        if (
          col.childs &&
          insertInChilds(col.childs, referenceElementId, newElement, position)
        ) {
          return tabs;
        }
      }
    }

    return tabs;
  } catch (error) {
    console.log('error in insert new element::', error);
    return tabStructure;
  }
};

export const insertGridInTabStructure = ({
  tabStructure,
  targetTabIndex,
  gridLayout,
  rowIndex = null,
  position = null,
}) => {
  const tabs = structuredClone(tabStructure);
  const rows = tabs[targetTabIndex].rows;

  if (rowIndex != null) {
    if (position === 'bottom') {
      rows.splice(rowIndex + 1, 0, gridLayout);
    } else {
      rows.splice(rowIndex, 0, gridLayout);
    }
  } else {
    if (position === 'top') {
      rows.unshift(gridLayout);
    } else {
      rows.push(gridLayout);
    }
  }
  return tabs;
};

export const insertElementInGridStructure = ({
  tabStructure,
  targetTabIndex,
  newElementId,
  rowIndex,
  colIndex,
  withChildContainer = false,
}) => {
  const tabs = JSON.parse(JSON.stringify(tabStructure));
  if (targetTabIndex < 0 || targetTabIndex >= tabs.length) return;
  const activeTab = tabs[targetTabIndex];

  const newElement = withChildContainer
    ? { elementId: newElementId, childs: [] }
    : { elementId: newElementId };

  const currentActiveRow = activeTab.rows[rowIndex];
  currentActiveRow.cols[colIndex] = {
    ...currentActiveRow.cols[colIndex],
    ...newElement,
  };

  return tabs;
};

export const removeElementFromTabStructure = ({
  tabStructure,
  targetTabIndex,
  elementIdToRemove,
}) => {
  try {
    const updatedTabList = JSON.parse(JSON.stringify(tabStructure));
    const removedElementIds = [];

    if (targetTabIndex < 0 || targetTabIndex >= updatedTabList.length) {
      return { updatedTabList, removedElementIds };
    }

    const activeTab = updatedTabList[targetTabIndex];

    function collectElementIds(element) {
      removedElementIds.push(element.elementId);
      if (element.childs) {
        for (const child of element.childs) {
          collectElementIds(child);
        }
      }
    }

    function removeFromColsOrChilds(cols, rowIndex) {
      for (let i = cols.length - 1; i >= 0; i--) {
        const col = cols[i];

        if (col.elementId === elementIdToRemove) {
          collectElementIds(col);
          if (cols.length > 1) {
            // Just clear the object, keep position
            cols[i] = {};
          } else {
            // Remove only if it's the last one
            cols.splice(i, 1);
          }

          if (cols.length === 0) {
            activeTab.rows.splice(rowIndex, 1);
          }

          return true;
        }

        if (col.childs && col.childs.length > 0) {
          for (let j = col.childs.length - 1; j >= 0; j--) {
            const child = col.childs[j];
            if (child.elementId === elementIdToRemove) {
              collectElementIds(child);
              col.childs.splice(j, 1);
            } else if (child.childs && child.childs.length > 0) {
              removeFromColsOrChilds([child], rowIndex);
            }
          }
        }
      }
    }

    for (let r = activeTab.rows.length - 1; r >= 0; r--) {
      removeFromColsOrChilds(activeTab.rows[r].cols, r);
    }

    return { updatedTabList, removedElementIds };
  } catch (error) {
    console.log('remove element from tab ::', error);
    return { updatedTabList: tabStructure, removedElementIds };
  }
};

export const moveOrRemoveTabFromTabStructure = ({
  tabList,
  deleteTabIndex,
  targetTabIndex = null,
}) => {
  try {
    const updatedTabList = JSON.parse(JSON.stringify(tabList));
    let removedTab = null;
    let removedElementIds = [];

    if (deleteTabIndex < 0 || deleteTabIndex >= updatedTabList.length) {
      return { updatedTabList, removedElementIds };
    }

    const sourceTab = updatedTabList[deleteTabIndex];

    if (
      targetTabIndex !== null &&
      targetTabIndex >= 0 &&
      targetTabIndex < updatedTabList.length
    ) {
      const targetTab = updatedTabList[targetTabIndex];
      if (sourceTab.rows && sourceTab.rows.length > 0) {
        targetTab.rows.push(...sourceTab.rows);
      }

      updatedTabList.splice(deleteTabIndex, 1);
    } else {
      removedTab = updatedTabList.splice(deleteTabIndex, 1)[0];

      function collectIdsFromCols(cols) {
        for (const col of cols) {
          removedElementIds.push(col.elementId);
          if (col.childs && col.childs.length > 0) {
            collectIdsFromCols(col.childs);
          }
        }
      }

      if (removedTab.rows) {
        for (const row of removedTab.rows) {
          if (row.cols && row.cols.length > 0) {
            collectIdsFromCols(row.cols);
          }
        }
      }
    }

    return { updatedTabList, removedElementIds };
  } catch (error) {
    console.log('move/remove tab:', error);
    return { updatedTabList: tabList, removedElementIds: [] };
  }
};

export const isColsHaveElements = (col) => {
  return col.hasOwnProperty('elementId');
};

export const isRowHaveColumnsWithElement = (row) => {
  // if (!row || !row.cols) return false;

  return row.cols.some((col) => col.hasOwnProperty('elementId'));
};

export const generateUUID = () => {
  return Math.round(Math.random() * 100000 + new Date().getTime());
};
const getIncrementNum = (type, existingElements) => {
  let currentNums = [];
  let typeArr = existingElements.filter((e) => e.type == type);

  if (typeArr.length > 0) {
    typeArr.forEach((e) => {
      let num = e.name.match(/\d+/g);
      if (!isNaN(parseInt(num))) currentNums.push(num[0]);
    });

    if (currentNums.length == 0) return 1;
    return Math.max(...currentNums) + 1;
  } else return 1;
};
export const getNewFormElement = (elementDefaults, existingElements) => {
  const elementBase = {
    id: generateUUID(),
    settings: {
      default: false,
      description: null,
      required: true,
      disabled: false,
      prefixIcon: { icon: null, linkType: null },
    },
  };

  if (['file', 'radio', 'checkbox', 'filenew'].includes(elementDefaults.type)) {
    delete elementBase.settings.default;
  }

  // Create deepcopies of both elements
  const elBase = JSON.parse(JSON.stringify(elementBase));
  const elDefaults = JSON.parse(JSON.stringify(elementDefaults));

  // Merge base & predefined values
  const mergeElement = {
    ...elBase,
    ...elDefaults,
    settings: { ...elBase.settings, ...elDefaults.settings },
  };

  // Add tabindex to all non-layout items
  if (!elementDefaults.settings.layoutItem) mergeElement.settings.tabindex = '';

  // Create copy of final element
  const finalElement = JSON.parse(JSON.stringify(mergeElement));

  // Add unique number to el.name
  const incNum = getIncrementNum(finalElement.type, existingElements);
  finalElement.name += incNum;
  finalElement.label += incNum;

  return finalElement;
};
const makeCloneElement = (elements, idToClone) => {
  const existingNames = new Set(elements.map((e) => e.name));
  const existingLabels = new Set(elements.map((e) => e.label));
  const originalElement = elements.find((el) => el.id === idToClone);
  if (!originalElement) return null;
  const newElement = JSON.parse(JSON.stringify(originalElement));
  newElement.id = generateUUID();
  newElement.name = createUnique(Array.from(existingNames), newElement.name);
  newElement.label = createUnique(Array.from(existingLabels), newElement.label);
  return newElement;
};

export const duplicateElementInTabStructure = ({
  tabList,
  elementList,
  activeTabIndex,
  elementIdToDuplicate,
  isDuplicateInCol = false,
}) => {
  try {
    let updatedTabList = JSON.parse(JSON.stringify(tabList));
    const updatedElementList = JSON.parse(JSON.stringify(elementList));
    const activeTab = updatedTabList[activeTabIndex];
    if (!activeTab) return { updatedTabList, updatedElementList };

    function deepDuplicate(originalElement) {
      if (!originalElement) return null;

      const duplicateElement = makeCloneElement(
        updatedElementList,
        originalElement.elementId
      );
      if (duplicateElement) updatedElementList.push(duplicateElement);

      const newId = duplicateElement?.id;
      let duplicatedChildren = [];

      if (originalElement.childs && originalElement.childs.length > 0) {
        for (const child of originalElement.childs) {
          const dupChild = deepDuplicate(child);
          if (Array.isArray(dupChild)) {
            duplicatedChildren.push(...dupChild);
          } else if (dupChild) {
            duplicatedChildren.push(dupChild);
          }
        }
      }

      if (!newId) {
        return duplicatedChildren.length > 0 ? duplicatedChildren : null;
      }

      return {
        ...originalElement,
        elementId: newId,
        ...(originalElement.childs
          ? { childs: duplicatedChildren.length > 0 ? duplicatedChildren : [] }
          : {}),
      };
    }

    let pendingRowInsertions = [];

    function processElement(element, rowIndex, level = 'col') {
      if (element.elementId === elementIdToDuplicate) {
        const duplicated = deepDuplicate(element);
        if (!duplicated) return [element];

        if (level === 'col') {
          if (isDuplicateInCol) {
            return [element, duplicated];
          } else {
            pendingRowInsertions.push({
              index: rowIndex + 1,
              row: { cols: [duplicated] },
            });
            return [element];
          }
        } else {
          return [element, duplicated];
        }
      }

      let newChilds = element.childs
        ? element.childs
            .map((child) => processElement(child, rowIndex, 'child'))
            .flat()
        : [];

      return [
        {
          ...element,
          ...(element.childs ? { childs: newChilds } : {}),
        },
      ];
    }

    let newRows = activeTab.rows.map((row, rowIndex) => {
      const newCols = row.cols
        .map((col) => processElement(col, rowIndex, 'col'))
        .flat();
      return { ...row, cols: newCols };
    });

    if (pendingRowInsertions.length > 0) {
      let withInserted = [];
      newRows.forEach((row, idx) => {
        withInserted.push(row);
        pendingRowInsertions
          .filter((ins) => ins.index === idx + 1)
          .forEach((ins) => withInserted.push(ins.row));
      });
      newRows = withInserted;
    }

    updatedTabList = updatedTabList.map((t, i) =>
      i === activeTabIndex ? { ...t, rows: newRows } : t
    );

    return { updatedTabList, updatedElementList };
  } catch (error) {
    console.log('duplicate element in tab :', error);
    return { updatedTabList: tabList, updatedElementList: elementList };
  }
};

export const parseElementId = (idString) => {
  const regex = /^(.*?)-(\d{6,})$/;

  const match = idString.match(regex);
  if (!match) return { id: null, property: null, index: null };

  const fullBeforeId = match[1];
  const numericId = Number(match[2]);
  let property = null;
  let index = null;

  const complexRegex = /(?:^|[\w-]+-)(\d+)-([\w-]+)$/;
  const simpleRegex = /([\w-]+)$/;

  if (complexRegex.test(fullBeforeId)) {
    const inner = fullBeforeId.match(complexRegex);
    index = Number(inner[1]);
    property = inner[2];
  } else if (simpleRegex.test(fullBeforeId)) {
    const inner = fullBeforeId.match(simpleRegex);
    property = inner[1];
  }

  return {
    id: numericId,
    property,
    index,
  };
};

export const getInputVariableList = (elements) => {
  const filterInputList = elements.filter(
    (element) => !['hidden'].includes(element.type)
  );
  const elementOptionList = filterInputList.reduce((optionList, element) => {
    if (
      ['checkbox', 'radio', 'select'].includes(element.type) &&
      element.options.length
    ) {
      optionList = optionList.concat(
        element.options
          .map((op) =>
            op.label
              ? {
                  ...op,
                  label: `${element.label}:${op.value}`,
                  name: op.value,
                }
              : null
          )
          .filter(() => Boolean)
      );
    }
    return optionList;
  }, []);

  return [...filterInputList, ...elementOptionList];
};

export const getElementSuggetionById = (elementId, getInputVariables) => {
  const elementParseId = parseElementId(elementId);
  const selectElement = getInputVariables.find(
    (v) => v?.id === elementParseId.id
  );
  if (selectElement) {
    if (
      ['checkbox', 'radio'].includes(selectElement.type) &&
      elementParseId.index !== null
    ) {
      const option = selectElement?.options?.[elementParseId.index];
      return {
        label: `${selectElement.name}:${option?.value || ''}-${
          elementParseId.property
        }`,
        name: elementId,
        displayName: `${option?.value || ''}-${elementParseId.property}`,
        type: 'id',
      };
    }
    return {
      label: `${selectElement.name}-${elementParseId.property}`,
      name: elementId,
      displayName: `${selectElement.name}-${elementParseId.property}`,
      type: 'id',
    };
  }
  return null;
};

export const getNewFormElementID = () => {
  return Math.round(Math.random() * 100000 + new Date().getTime());
};
